import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { result } from 'underscore';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SWITCH_BACK_NAV_REFRESH_KEY } from '@bingads-webui-react/preference-utility';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import navRefreshImage from './navrefresh.jpg';

const MillisecondsInADay = 86400000;
const LastDayToSwitchBack = new Date(2024, 7, 1); // August 1, 2024
const NavigationRefreshDialogPreference = 'NavigationRefreshDialogPreference';
const OneWeek = 'OneWeek';
const OneMonth = 'OneMonth';
const Further = 'Further';
const Past = 'Past';

const getTimePeriod = () => {
  const now = new Date();

  if (now > LastDayToSwitchBack) {
    return Past;
  }

  if (LastDayToSwitchBack - now < MillisecondsInADay * 7) {
    return OneWeek;
  }

  if (LastDayToSwitchBack - now < MillisecondsInADay * 30) {
    return OneMonth;
  }

  return Further;
};

const useDialogStyles = makeStyles({
  navRefreshImage: {
    backgroundImage: `url(${navRefreshImage})`,
    height: '271px',
    marginBottom: tokens.spacingVerticalXXL,
  },
  button: {
    whiteSpace: 'nowrap',
  },
  footer: {
    justifyContent: 'space-between',
    width: '100%',
  },
  singleButtonFooter: {
    justifyContent: 'flex-end',
  },
});

export const NavigationRefreshDialog = () => {
  const classes = useDialogStyles();
  const i18n = useContext(I18nContext);
  const preferenceService = useContext(PreferenceContext);
  const [open, setOpen] = React.useState(false);
  const [disableButtons, setDisableButtons] = React.useState(false);
  const instrumentation = useContext(InstrumentationContext);
  const permissions = useContext(PermissionsContext);
  const logger = instrumentation.createScenarioAndActivity({
    scenarioName: 'navigationRefreshDialog',
    activityName: 'navigationRefreshDialog',
  }).activity;
  const switchBackNavRefreshPreference = preferenceService.findByNameAtUserLevel(SWITCH_BACK_NAV_REFRESH_KEY);
  const switchBackNavRefresh = result(switchBackNavRefreshPreference, 'Data', false);
  const dialogPreference = preferenceService.findByNameAtUserLevel(NavigationRefreshDialogPreference) || {};
  const dismissDialog = result(dialogPreference, 'Data', {});
  const timePeriod = useMemo(() => getTimePeriod(), []);
  const isPastCutOff = timePeriod === Past;
  const messageTemplate =
    i18n.getString(_TL_('Start by browsing the navigation menu on the left, which provides a cleaner look and feel. If you can\'t find something, select {{b}}Tools{{/b}}. {{a}}Learn more about how to get around.{{/a}}'));

  const updatePreference = useCallback(() => {
    const newDismissDialog = {
      ...dismissDialog,
      [timePeriod]: true,
    };

    preferenceService.setAtUserLevel({
      Name: NavigationRefreshDialogPreference,
      Data: newDismissDialog,
    }).then(({ Id }) => {
      preferenceService.onRemoteSyncDone(Id).then(() => {
        logger.trace({
          preference: `${NavigationRefreshDialogPreference} updated to ${JSON.stringify(newDismissDialog)}`,
        });
      });
    });
  }, [dismissDialog, logger, preferenceService, timePeriod]);

  useEffect(() => {
    if (switchBackNavRefresh && permissions.dynamic.NavigationRefresh) {
      if (timePeriod === Past ||
          (timePeriod === OneWeek && !dismissDialog[OneWeek]) ||
          (timePeriod === OneMonth && !dismissDialog[OneMonth]) ||
          (timePeriod === Further && !dismissDialog[Further])) {
        setOpen(true);
        logger.trace(`NavigationRefreshDialog: dialog opened for time period ${timePeriod}`);
        setClarityTag({ NavigationRefreshDialog: 'true' });
      }
    }
  }, [dismissDialog, logger, permissions.dynamic.NavigationRefresh, preferenceService, switchBackNavRefresh, timePeriod]);

  const onStay = useCallback(() => {
    updatePreference();
    setOpen(false);
  }, [updatePreference]);

  const onTry = useCallback((e) => {
    setDisableButtons(true);
    e.preventDefault();
    if (dialogPreference && dialogPreference.Id) {
      preferenceService.delAtUserLevel(dialogPreference.Id);
    }

    if (!switchBackNavRefresh) {
      window.location.reload();
      return;
    }

    preferenceService.setAtUserLevel({
      Name: SWITCH_BACK_NAV_REFRESH_KEY,
      Data: false,
    }).then(({ Id }) => {
      preferenceService.onRemoteSyncDone(Id).then(() => {
        logger.trace({
          preference: `${SWITCH_BACK_NAV_REFRESH_KEY} set to false from navigation refresh dialog with NavigationRefresh enabled`,
        });
        setClarityTag({ [SWITCH_BACK_NAV_REFRESH_KEY]: 'false' });

        if (timePeriod !== Past) {
          window.location.reload();
        } else {
          setOpen(false);
        }
      });
    });
  }, [dialogPreference, logger, preferenceService, switchBackNavRefresh, timePeriod]);

  return (
    <Dialog modalType="modal" open={open} className="navrefresh-dialog">
      <DialogSurface>
        <UiBlocker
          blocking={disableButtons}
          loadingText={i18n.getString(_TL_('Loading...'))}
          classes={{
              blockUiContainer: 'blocker-global',
              blockerIndicator: 'blocker-indicator',
            }}
        >
          <div className={classes.navRefreshImage} />
          <DialogBody>
            <DialogTitle>
              {isPastCutOff ?
                i18n.getString(_TL_('Welcome to the new Microsoft Advertising navigation experience')) :
                i18n.getString(_TL_('Switch to the new Microsoft Advertising navigation experience today'))
              }
            </DialogTitle>
            <DialogContent>
              {
                isPastCutOff ?
                  <p>
                    {i18n.getString(_TL_('We\'ve updated the Microsoft Advertising navigation experience to help you maximize your efficiency while you\'re managing your account. We think you\'ll like how intuitive and customizable this new experience is. '))}
                    <ReactTemplate
                      model={{
                      b: content => <strong>{content}</strong>,
                      a: content => <a href="https://go.microsoft.com/fwlink/?LinkId=2257422" target="_blank" rel="noreferrer">{content}</a>,
                    }}
                      template={messageTemplate}
                    />
                  </p> :
                  <>
                    <p>
                      { i18n.getString(_TL_('We\'re delighted to provide you with a new  Microsoft Advertising navigation experience designed to maximize your efficiency. We think you\'ll like how user-friendly and customizable it is. Give it a try!')) }
                    </p>
                    <p>
                      { i18n.getString(_TL_('You can switch back to the old navigation experience until August 1st, at which time, everyone will be locked into the new navigation experience.')) }
                    </p>
                  </>
              }
            </DialogContent>
            <DialogActions className={mergeClasses('footer', classes.footer, isPastCutOff ? classes.singleButtonFooter : '')} fluid >
              <DialogTrigger disableButtonEnhancement>
                {!isPastCutOff &&
                  <Button appearance="secondary" className={mergeClasses('cancel', classes.button)} onClick={onStay} disabled={disableButtons}>
                    {i18n.getString(_TL_('Stay in the old experience'))}
                  </Button>
                }
              </DialogTrigger>
              <Button appearance="primary" className={mergeClasses('accept', classes.button)} onClick={onTry} disabled={disableButtons}>
                { isPastCutOff ? i18n.getString(_TL_('Got it')) : i18n.getString(_TL_('Try the new navigation experience'))}
              </Button>
            </DialogActions>
          </DialogBody>
        </UiBlocker>
      </DialogSurface>
    </Dialog>
  );
};
